import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { persistStorage } from '@/lib/storage'

export interface Pair {
  from: string
  to: string
}

interface ConverterState {
  from: string
  to: string
  amount: string
  favorites: string[]
  savedPairs: Pair[]
  setFrom: (code: string) => void
  setTo: (code: string) => void
  setAmount: (amount: string) => void
  swap: () => void
  setPair: (pair: Pair) => void
  toggleFavorite: (code: string) => void
  togglePair: (pair: Pair) => void
}

export const CONVERTER_KEY = 'gx-converter'

const samePair = (a: Pair, b: Pair) => a.from === b.from && a.to === b.to

export const useConverter = create<ConverterState>()(
  persist(
    (set) => ({
      from: 'USD',
      to: 'EUR',
      amount: '1000',
      favorites: ['USD', 'EUR', 'SAR', 'AED', 'GBP', 'TRY'],
      savedPairs: [],
      setFrom: (code) => set((s) => (code === s.to ? { from: code, to: s.from } : { from: code })),
      setTo: (code) => set((s) => (code === s.from ? { to: code, from: s.to } : { to: code })),
      setAmount: (amount) => set({ amount }),
      swap: () => set((s) => ({ from: s.to, to: s.from })),
      setPair: ({ from, to }) => set({ from, to }),
      toggleFavorite: (code) =>
        set((s) => ({
          favorites: s.favorites.includes(code)
            ? s.favorites.filter((c) => c !== code)
            : [...s.favorites, code],
        })),
      togglePair: (pair) =>
        set((s) => ({
          savedPairs: s.savedPairs.some((p) => samePair(p, pair))
            ? s.savedPairs.filter((p) => !samePair(p, pair))
            : [pair, ...s.savedPairs].slice(0, 12),
        })),
    }),
    {
      name: CONVERTER_KEY,
      storage: persistStorage,
      version: 1,
      partialize: ({ from, to, amount, favorites, savedPairs }) => ({
        from,
        to,
        amount,
        favorites,
        savedPairs,
      }),
    },
  ),
)

export const isSavedPair = (pairs: Pair[], pair: Pair) => pairs.some((p) => samePair(p, pair))
